import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import type { NotificationSettings } from "@/hooks/useNotificationSettings";
import { Crown, Lock, Mail } from "lucide-react";
import { WaitlistModal } from "@/components/shared/WaitlistModal";
import { useAuth } from "@/hooks/useAuth";

interface EmailConfigProps {
  settings: NotificationSettings;
  onSave: (updates: Partial<NotificationSettings>) => Promise<void>;
  isPro?: boolean;
}

export function EmailConfig({ settings, onSave, isPro = false }: EmailConfigProps) {
  const { user } = useAuth();
  const authEmail = user?.email || "";

  const [host, setHost] = useState(settings.email_smtp_host || "");
  const [port, setPort] = useState(String(settings.email_smtp_port || 587));
  const [user2, setUser2] = useState(settings.email_smtp_user || "");
  const [password, setPassword] = useState(settings.email_smtp_password || "");
  const [fromAddr, setFromAddr] = useState(settings.email_from_address || "");
  const [toAddr, setToAddr] = useState(settings.email_to_address || "");
  const [enabled, setEnabled] = useState(settings.email_enabled ?? true);
  const [waitlistOpen, setWaitlistOpen] = useState(false);

  useEffect(() => {
    setHost(settings.email_smtp_host || "");
    setPort(String(settings.email_smtp_port || 587));
    setUser2(settings.email_smtp_user || "");
    setPassword(settings.email_smtp_password || "");
    setFromAddr(settings.email_from_address || "");
    setToAddr(settings.email_to_address || "");
    setEnabled(settings.email_enabled ?? true);
  }, [settings]);

  const handleSave = async () => {
    // Safety check: At least one must be enabled
    if (!enabled && !settings.discord_enabled && !settings.telegram_enabled) {
      toast.error("Security Override: At least one notification channel must be active.");
      setEnabled(true);
      return;
    }

    try {
      await onSave({
        email_smtp_host: host || null,
        email_smtp_port: parseInt(port) || 587,
        email_smtp_user: user2 || null,
        email_smtp_password: password || null,
        email_from_address: fromAddr || null,
        email_to_address: toAddr || null,
        email_enabled: enabled,
      });
      toast.success("Email settings saved");
    } catch (err: any) {
      toast.error(err.message || "Failed to save email settings");
    }
  };

  return (
    <div className="space-y-6">
      <div className="border-4 border-foreground p-6 bg-accent/20">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-xl font-black uppercase tracking-tighter text-foreground">Standard Signal Relay</h3>
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mt-1 max-w-sm">
              Receive instant downtime alerts to your inbox via the UptimeWing Global Network.
            </p>
          </div>
          <Switch checked={enabled} onCheckedChange={setEnabled} className="border-2 border-foreground shadow-[2px_2px_0_0_#000]" />
        </div>

        <div className="space-y-2 mb-4">
          <Label className="text-[10px] font-black uppercase tracking-widest text-foreground">Destination Email</Label>
          <div className="relative">
            <Input
              value={toAddr}
              onChange={(e) => setToAddr(e.target.value)}
              placeholder={authEmail || "your@email.com"}
              className="h-12 rounded-none border-2 border-foreground bg-background text-sm font-bold shadow-xs focus:ring-0 pr-32"
            />
            {authEmail && !toAddr && (
              <button
                type="button"
                onClick={() => setToAddr(authEmail)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] font-black uppercase tracking-widest bg-foreground text-background px-2 py-1 hover:bg-primary hover:text-primary-foreground transition-all flex items-center gap-1"
              >
                <Mail className="h-3 w-3" /> Use login email
              </button>
            )}
          </div>
          <p className="text-[9px] font-bold text-muted-foreground uppercase mt-1">
            {toAddr ? (
              <span className="text-status-up">✓ Alerts will be sent to: {toAddr}</span>
            ) : authEmail ? (
              <span>Leave empty to auto-send to <strong>{authEmail}</strong> (your login email).</span>
            ) : (
              "Enter an email address to receive alerts."
            )}
          </p>
        </div>

        <Button onClick={handleSave} className="w-full h-11 rounded-none border-2 border-foreground bg-primary text-primary-foreground font-black uppercase tracking-tighter shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all">
          Deploy Email Settings
        </Button>
      </div>

      <div className="border-4 border-foreground bg-background border-dashed p-6 relative overflow-hidden">
        {/* Pro Paywall Overlay */}
        {!isPro && (
          <div className="absolute inset-0 z-10 bg-background/40 backdrop-blur-[1.5px] flex flex-col items-center justify-center p-6 text-center">
            <div className="bg-background border-4 border-foreground p-6 shadow-brutal max-w-sm flex flex-col items-center justify-center">
              <div className="h-12 w-12 border-4 border-primary flex items-center justify-center bg-primary text-primary-foreground mb-4">
                <Lock className="h-6 w-6" />
              </div>
              <div className="flex items-center gap-2 mb-2">
                <Crown className="h-4 w-4 text-primary" />
                <span className="text-xs font-black uppercase tracking-widest text-primary">Founder Pack Feature</span>
              </div>
              <p className="text-[10px] font-bold text-muted-foreground uppercase leading-relaxed max-w-xs mb-4">
                Custom SMTP lets you send alerts from your own domain. Available exclusively on the Founder Pack.
              </p>
              <Button
                onClick={() => setWaitlistOpen(true)}
                className="rounded-none border-2 border-foreground bg-primary text-primary-foreground font-black uppercase tracking-tighter shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all flex items-center gap-2 h-10 text-xs"
              >
                <Crown className="h-4 w-4" />
                Unlock with Founder Pack
              </Button>
            </div>
          </div>
        )}

        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <Crown className="h-3 w-3 text-primary" />
            <h3 className="text-sm font-black uppercase tracking-widest text-foreground">White-label Custom SMTP</h3>
            <span className="text-[9px] font-black uppercase px-2 py-0.5 bg-primary text-primary-foreground">Founder</span>
          </div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mt-1">
            Bypass our relay. Send alerts directly from your own domain (e.g., alerts@yourcompany.com).
          </p>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase tracking-widest">SMTP Host</Label>
              <Input value={host} onChange={(e) => setHost(e.target.value)} placeholder="smtp.mailgun.org" className="h-10 rounded-none border-2 border-foreground bg-background text-xs font-mono shadow-xs" disabled={!isPro} />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase tracking-widest">Port</Label>
              <Input value={port} onChange={(e) => setPort(e.target.value)} placeholder="587" className="h-10 rounded-none border-2 border-foreground bg-background text-xs font-mono shadow-xs" disabled={!isPro} />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase tracking-widest">Username</Label>
              <Input value={user2} onChange={(e) => setUser2(e.target.value)} className="h-10 rounded-none border-2 border-foreground bg-background text-xs font-mono shadow-xs" disabled={!isPro} />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase tracking-widest">Password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-10 rounded-none border-2 border-foreground bg-background text-xs font-mono shadow-xs" disabled={!isPro} />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-[10px] font-black uppercase tracking-widest">From Address</Label>
            <Input value={fromAddr} onChange={(e) => setFromAddr(e.target.value)} placeholder="Mission Control <alerts@yourcompany.com>" className="h-10 rounded-none border-2 border-foreground bg-background text-xs shadow-xs" disabled={!isPro} />
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <Button onClick={handleSave} disabled={!isPro} className="flex-1 h-11 rounded-none border-2 border-foreground bg-foreground text-background font-black uppercase tracking-widest shadow-xs hover:bg-muted-foreground transition-all disabled:opacity-50">
              Lock Custom SMTP
            </Button>
          </div>
        </div>
      </div>
      <WaitlistModal open={waitlistOpen} onOpenChange={setWaitlistOpen} initialPlan="founder" />
    </div>
  );
}

